import React, { useEffect, useState } from 'react';
import { getCurrentUser } from '../services/authService';
import { getUserSettings, saveSettings, Settings as SettingsType, getLanes, Lane, getCarriers } from '../services/dbService';
import { scheduleEmailScanning, processEmailsForReceipts } from '../services/emailService';

const Settings: React.FC = () => {
  const [settings, setSettings] = useState<SettingsType | null>(null);
  const [lanes, setLanes] = useState<Lane[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [scanResult, setScanResult] = useState<string | null>(null);
  const [selectedCarrierId, setSelectedCarrierId] = useState<string>('');
  const [carriers, setCarriers] = useState<Array<{ id: string; name: string }>>([]);
  const [scanHoursBack, setScanHoursBack] = useState<number>(24); // Default to scanning 24 hours back
  
  // Form state
  const [scanInterval, setScanInterval] = useState<number>(2); // Default to 2 hours
  const [selectedLanes, setSelectedLanes] = useState<string[]>([]);
  
  // Get current user
  const user = getCurrentUser();
  
  useEffect(() => {
    const fetchData = async () => {
      try {
        if (!user) {
          throw new Error('User not authenticated');
        }
        
        // Fetch user settings
        const userSettings = await getUserSettings(user.uid);
        
        // Fetch available lanes
        const availableLanes = await getLanes();
        setLanes(availableLanes);
        
        // Fetch carriers
        const allCarriers = await getCarriers();
        setCarriers(allCarriers.map(c => ({ id: c.id!, name: c.name })));
        
        if (allCarriers.length > 0 && !selectedCarrierId) {
          setSelectedCarrierId(allCarriers[0].id!);
        }
        
        // Set form state from user settings
        if (userSettings) {
          setSettings(userSettings);
          setScanInterval(userSettings.scanInterval || 2);
          setSelectedLanes(userSettings.lanes || []);
        }
      } catch (err: any) {
        console.error('Error fetching settings:', err);
        setError(err.message || 'Failed to load settings');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [user, selectedCarrierId]);
  
  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      if (!user) {
        throw new Error('User not authenticated');
      }
      
      setSaving(true);
      
      // Create settings object
      const updatedSettings: SettingsType = {
        userId: user.uid,
        lanes: selectedLanes,
        scanInterval: scanInterval || 2, // Default to 2 if undefined
        createdAt: settings?.createdAt || new Date(),
        updatedAt: new Date()
      };
      
      // Save settings to database
      await saveSettings(updatedSettings);
      
      // Update local state
      setSettings(updatedSettings);
      
      // Show success message
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err: any) {
      console.error('Error saving settings:', err);
      setError(err.message || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };
  
  const handleManualScan = async () => {
    try {
      if (!user || !selectedCarrierId) {
        throw new Error('User not authenticated or no carrier selected');
      }
      
      setScanning(true);
      setScanResult(null);
      
      // Clear previous result
      const selectedCarrier = carriers.find(c => c.id === selectedCarrierId);
      
      if (!selectedCarrier) {
        throw new Error('Selected carrier not found');
      }
      
      // Process emails for receipts with the specified timeframe
      const count = await processEmailsForReceipts(
        user.uid, 
        selectedCarrierId, 
        selectedCarrier.name,
        scanHoursBack  // Pass hours to look back
      );
      
      // Show result
      setScanResult(`Scan completed: ${count} new receipt${count !== 1 ? 's' : ''} created from the past ${scanHoursBack} hours.`);
    } catch (err: any) {
      console.error('Error during manual scan:', err);
      setError(err.message || 'Failed to perform manual scan');
    } finally {
      setScanning(false);
    }
  };
  
  const toggleLane = (laneId: string) => {
    if (selectedLanes.includes(laneId)) {
      setSelectedLanes(selectedLanes.filter(id => id !== laneId));
    } else {
      setSelectedLanes([...selectedLanes, laneId]);
    }
  };
  
  if (loading) {
    return <div className="loading">Loading settings...</div>;
  }
  
  return (
    <div className="settings-page">
      <h1>NFI Lumper Receipt Creator | Settings</h1>
      
      {error && <div className="error-message">{error}</div>}
      {saveSuccess && <div className="success-message">Settings saved successfully!</div>}
      {scanResult && <div className="success-message">{scanResult}</div>}
      
      <form onSubmit={handleSave}>
        <div className="settings-section">
          <h2>Email Scanning Configuration</h2>
          
          <div className="service-unavailable-notice">
            <p><strong>⚠️ Email scanning functionality is currently unavailable.</strong></p>
            <p>Please use manual receipt creation or batch processing instead.</p>
          </div>
          
          <div className="form-group disabled">
            <label htmlFor="scanInterval">Automatic Scan Interval</label>
            <select 
              id="scanInterval"
              value={scanInterval}
              onChange={(e) => setScanInterval(Number(e.target.value))}
              className="form-control"
              disabled
            >
              <option value="1">Every 1 hour</option>
              <option value="2">Every 2 hours</option>
              <option value="4">Every 4 hours</option>
              <option value="6">Every 6 hours</option>
              <option value="12">Every 12 hours</option>
              <option value="24">Every 24 hours</option>
            </select>
            <p className="help-text">
              Feature currently unavailable.
            </p>
          </div>
          
          <div className="settings-info">
            <p><i className="fas fa-info-circle"></i> The system will automatically scan emails at the selected interval to generate receipts for matching lanes.</p>
          </div>
        </div>
        
        <div className="settings-section">
          <h2>Lanes to Monitor</h2>
          <p className="help-text">
            Select which lanes to monitor for incoming emails
          </p>
          
          {lanes.length === 0 ? (
            <p className="no-data">No lanes configured. Please add lanes first.</p>
          ) : (
            <div className="lanes-selection">
              {lanes.map(lane => (
                <div key={lane.id} className="lane-checkbox">
                  <label>
                    <input 
                      type="checkbox"
                      checked={selectedLanes.includes(lane.id!)}
                      onChange={() => toggleLane(lane.id!)}
                      disabled={!lane.isActive}
                    />
                    <span className={!lane.isActive ? 'inactive-lane' : ''}>
                      {lane.origin} → {lane.destination} (${typeof lane.price === 'number' ? lane.price.toFixed(2) : Number(lane.price || 140).toFixed(2)})
                      {!lane.isActive && ' (Inactive)'}
                    </span>
                  </label>
                </div>
              ))}
            </div>
          )}
        </div>
        
        <div className="settings-actions">
          <button 
            type="submit" 
            className="save-btn" 
            disabled={saving || lanes.length === 0}
          >
            {saving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </form>
      
      <div className="settings-section">
        <h2>Manual Email Scan</h2>
        
        <div className="service-unavailable-notice">
          <p><strong>⚠️ Email scanning functionality is currently unavailable.</strong></p>
          <p>Please use manual receipt creation or batch processing instead.</p>
        </div>
        
        <div className="manual-scan-container">
          <div className="form-group disabled">
            <label htmlFor="carrierSelect">Select Carrier:</label>
            <select 
              id="carrierSelect"
              value={selectedCarrierId}
              onChange={(e) => setSelectedCarrierId(e.target.value)}
              disabled
            >
              {carriers.length === 0 ? (
                <option value="">No carriers available</option>
              ) : (
                carriers.map(carrier => (
                  <option key={carrier.id} value={carrier.id}>
                    {carrier.name}
                  </option>
                ))
              )}
            </select>
          </div>
          
          <div className="form-group disabled">
            <label htmlFor="hoursBack">Scan emails from past:</label>
            <select 
              id="hoursBack"
              value={scanHoursBack}
              onChange={(e) => setScanHoursBack(Number(e.target.value))}
              disabled
              className="form-control"
            >
              <option value="12">12 hours</option>
              <option value="24">24 hours</option>
              <option value="48">48 hours</option>
              <option value="72">72 hours</option>
              <option value="168">7 days</option>
            </select>
          </div>
          
          <button 
            className="scan-btn disabled" 
            onClick={handleManualScan}
            disabled
          >
            Scan Now
          </button>
        </div>
      </div>
      
      <div className="settings-section">
        <h2>Account Information</h2>
        <p>
          <strong>Email:</strong> {user?.email}
        </p>
      </div>
    </div>
  );
};

export default Settings; 